/**
 * Family store — scaffold.
 *
 * Responsibilities:
 *   - Own the Family record (ordering, sharedFamilyMode, per-child prefs).
 *   - Mirror `activeChildId` with childStore (single source of truth for
 *     "who is driving" stays in childStore; we subscribe/write through).
 *   - Provide a profile-switcher API: list, swap, reorder.
 *   - Host sibling co-op sessions + a derivation helper for ageGap hints.
 *   - Migrate legacy single-child installs (pre-family) on first load.
 *
 * NOTE ON LOCATION: the rest of the codebase puts stores under
 * `src/store/` (singular). This file is intentionally placed under
 * `src/stores/` per the task spec. A one-line re-export from
 * `src/store/familyStore.ts` can bridge if needed — left for a follow-up
 * so we don't churn existing import paths in this scaffold.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ChildPreferences,
  CoopMode,
  CoopRole,
  CoopSession,
  Family,
  PrivacyLevel,
} from '../types/family';
import { MAX_CHILDREN_PER_FAMILY } from '../types/family';
import type { AgeGroup, Child } from '../types/child';
import { useChildStore } from '../store/childStore';

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

/**
 * Privacy defaults by age group. Parents can override per-child.
 * Rationale: younger kids benefit from adult co-review; older kids get
 * agency over their own journals (trust-ladder).
 */
const defaultPrivacyByAge: Record<AgeGroup, PrivacyLevel> = {
  'pre-k': 'shared',
  kinder: 'shared',
  'early-elementary': 'parent-visible',
  'upper-elementary': 'private',
};

const defaultCoopRoleByAge: Record<AgeGroup, CoopRole> = {
  'pre-k': 'participant',
  kinder: 'participant',
  'early-elementary': 'flexible',
  'upper-elementary': 'leader',
};

export function defaultPreferencesFor(child: Child): ChildPreferences {
  return {
    privacyLevel: defaultPrivacyByAge[child.ageGroup] ?? 'parent-visible',
    siblingCoopOptIn: true,
    preferredCoopRole: defaultCoopRoleByAge[child.ageGroup] ?? 'flexible',
  };
}

// ---------------------------------------------------------------------------
// Store shape
// ---------------------------------------------------------------------------

interface FamilyState {
  family: Family;
  coopSessions: CoopSession[];

  // Profile switcher
  setActiveChild: (childId: string) => void;
  reorderChildren: (orderedChildIds: string[]) => void;
  toggleSharedFamilyMode: () => void;

  // Preferences
  setChildPreferences: (childId: string, patch: Partial<ChildPreferences>) => void;
  getChildPreferences: (childId: string) => ChildPreferences;

  // Sibling co-op
  startCoopSession: (
    activityId: string,
    mode: CoopMode,
    participants: CoopSession['participants']
  ) => CoopSession;
  completeCoopSession: (sessionId: string, parentReflection?: string) => void;

  // Admin
  setFamilyLabel: (label: string) => void;
  /**
   * Reconcile `family.childIds` with `childStore.children`. Call on boot
   * and after any child add/delete. Idempotent.
   */
  syncWithChildStore: () => void;

  /**
   * One-shot migration from pre-family installs. Runs automatically on
   * rehydrate; exposed here so tests can drive it deterministically.
   */
  migrateFromSingleChildIfNeeded: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyFamily(): Family {
  return {
    id: crypto.randomUUID(),
    createdAt: new Date().toISOString(),
    childIds: [],
    activeChildId: null,
    sharedFamilyMode: false,
    preferencesByChild: {},
  };
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useFamilyStore = create<FamilyState>()(
  persist(
    (set, get) => ({
      family: emptyFamily(),
      coopSessions: [],

      setActiveChild: (childId) => {
        // Mirror into childStore so all existing selectors keep working.
        useChildStore.getState().setActiveChild(childId);
        set((s) => ({ family: { ...s.family, activeChildId: childId } }));
      },

      reorderChildren: (orderedChildIds) => {
        // Trust the UI to pass a permutation of existing childIds; still
        // guard against missing/extra entries to avoid losing a child
        // from the avatar row after a race with add/delete.
        const existing = new Set(useChildStore.getState().children.map((c) => c.id));
        const next = orderedChildIds.filter((id) => existing.has(id));
        // Append any known children that weren't in the ordering (e.g.
        // a new child added in another tab).
        for (const id of existing) {
          if (!next.includes(id)) next.push(id);
        }
        set((s) => ({ family: { ...s.family, childIds: next } }));
      },

      toggleSharedFamilyMode: () =>
        set((s) => ({
          family: { ...s.family, sharedFamilyMode: !s.family.sharedFamilyMode },
        })),

      setChildPreferences: (childId, patch) => {
        set((s) => {
          const current = s.family.preferencesByChild[childId];
          // If we've never initialised prefs for this child (e.g. freshly
          // added before syncWithChildStore ran), fall back to the age
          // default so we don't write an incomplete object.
          const child = useChildStore.getState().children.find((c) => c.id === childId);
          const base = current ?? (child ? defaultPreferencesFor(child) : null);
          if (!base) return s;
          return {
            family: {
              ...s.family,
              preferencesByChild: {
                ...s.family.preferencesByChild,
                [childId]: { ...base, ...patch },
              },
            },
          };
        });
      },

      getChildPreferences: (childId) => {
        const existing = get().family.preferencesByChild[childId];
        if (existing) return existing;
        const child = useChildStore.getState().children.find((c) => c.id === childId);
        // Return a defaulted, non-persisted value so callers can render
        // before syncWithChildStore has run. The first write through
        // setChildPreferences persists it.
        return child
          ? defaultPreferencesFor(child)
          : {
              privacyLevel: 'parent-visible',
              siblingCoopOptIn: true,
              preferredCoopRole: 'flexible',
            };
      },

      startCoopSession: (activityId, mode, participants) => {
        const session: CoopSession = {
          id: crypto.randomUUID(),
          activityId,
          mode,
          participants,
          startedAt: new Date().toISOString(),
        };
        set((s) => ({ coopSessions: [...s.coopSessions, session] }));
        return session;
      },

      completeCoopSession: (sessionId, parentReflection) => {
        set((s) => ({
          coopSessions: s.coopSessions.map((sess) =>
            sess.id === sessionId
              ? {
                  ...sess,
                  completedAt: new Date().toISOString(),
                  parentReflection,
                }
              : sess
          ),
        }));
      },

      setFamilyLabel: (label) => {
        const trimmed = label.slice(0, 80);
        set((s) => ({ family: { ...s.family, label: trimmed } }));
      },

      syncWithChildStore: () => {
        const children = useChildStore.getState().children;
        const activeChildId = useChildStore.getState().activeChildId;
        set((s) => {
          // 1. Ordering: keep the current order for known ids, append new
          //    ones at the end, drop any that no longer exist.
          const known = new Set(children.map((c) => c.id));
          const kept = s.family.childIds.filter((id) => known.has(id));
          for (const c of children) {
            if (!kept.includes(c.id)) kept.push(c.id);
          }
          // Respect the family cap — excess children past cap are still
          // reachable via childStore, just not shown in the avatar row.
          // Surfacing "you've hit the limit" is the UI's job.
          const capped = kept.slice(0, MAX_CHILDREN_PER_FAMILY);

          // 2. Preferences: ensure an entry exists for every visible child.
          const prefs = { ...s.family.preferencesByChild };
          for (const c of children) {
            if (!prefs[c.id]) prefs[c.id] = defaultPreferencesFor(c);
          }
          // Drop prefs for deleted children to avoid unbounded growth.
          for (const id of Object.keys(prefs)) {
            if (!known.has(id)) delete prefs[id];
          }

          // Validate activeChildId against the VISIBLE list (`capped`), not
          // all `known` children. A child beyond the display cap isn't in
          // `childIds`, so it can't be reached via either switcher — pointing
          // `family.activeChildId` at it would strand the profile. Falling
          // back to the first visible child keeps `family.activeChildId`
          // always inside `childIds` (or null).
          const visible = new Set(capped);
          return {
            family: {
              ...s.family,
              childIds: capped,
              activeChildId:
                activeChildId && visible.has(activeChildId)
                  ? activeChildId
                  : capped[0] ?? null,
              preferencesByChild: prefs,
            },
          };
        });
      },

      migrateFromSingleChildIfNeeded: () => {
        // Pre-family installs already used childStore for multiple children
        // (the current code supports `children: Child[]`). "Single-child
        // install" here means: no family record exists OR the family has
        // an empty childIds array but childStore has children. In either
        // case, lifting childStore into family scope is safe and lossless.
        const s = get();
        const children = useChildStore.getState().children;
        if (children.length > 0 && s.family.childIds.length === 0) {
          get().syncWithChildStore();
        }
      },
    }),
    {
      name: 'homeschool-family',
      version: 1,
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        state.migrateFromSingleChildIfNeeded();
        state.syncWithChildStore();
      },
    }
  )
);

// ---------------------------------------------------------------------------
// Selectors / helpers
// ---------------------------------------------------------------------------

/**
 * Derive whether a given pair of children can co-op on an activity whose
 * co-op metadata says it supports a given age gap. Ignores the activity
 * itself — callers combine this with the activity's `ageGapYears` range.
 */
export function ageGapYearsBetween(a: Child, b: Child): number {
  const ad = new Date(a.birthDate).getTime();
  const bd = new Date(b.birthDate).getTime();
  const ms = Math.abs(ad - bd);
  return ms / (365.25 * 24 * 60 * 60 * 1000);
}

/** All active sibling pairs in display order. Used by the co-op launcher. */
export function selectSiblingPairs(): Array<[Child, Child]> {
  const { childIds } = useFamilyStore.getState().family;
  const byId = new Map(useChildStore.getState().children.map((c) => [c.id, c]));
  const pairs: Array<[Child, Child]> = [];
  for (let i = 0; i < childIds.length; i++) {
    for (let j = i + 1; j < childIds.length; j++) {
      const a = byId.get(childIds[i]);
      const b = byId.get(childIds[j]);
      if (a && b) pairs.push([a, b]);
    }
  }
  return pairs;
}
